import { checkFiltersAreEmpty } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import { formatDateToYYYYMMDD } from "@/utils/dateUtils";
import {
  isCategoricalExclusionFilter,
  isCategoricalFilter,
  isNumericFilter,
  isDateFilter,
  getRelativeDatePresetRange,
} from "./filtersUtil";
import { GEO_DATA_TYPE, isGeoFilterCondition } from "./geoFilterUtil";
import type { DateFilterCondition } from "./filterTypes";
import { SortItem } from "../Sorting/types";

export const transformSortingToApiFormat = (
  sorting: SortItem[] | undefined,
): string[] => {
  if (!sorting || sorting.length === 0) {
    return [];
  }

  return sorting?.map((sort) => {
    if (sort.direction === "descending") {
      return `-${sort.field}`;
    }
    return sort.field;
  });
};

const createFilterWithBooleanCheck = (
  field: string,
  operator: string,

  value: any,
  defaultDataType: string,
) => {
  if (typeof value === "boolean") {
    return {
      field,
      data_type: "boolean",
      operator,
      value,
    };
  }

  if (field === "site") {
    //Coercing this until the BE changes site to text
    return {
      field,
      data_type: "text",
      operator,
      value: String(value),
    };
  }

  return {
    field,
    data_type: defaultDataType,
    operator,
    value,
  };
};

const buildExclusionApiFilters = (
  filterKey: string,
  condition: { condition: "not_in"; values: any[] },
  apiDataType: string,
) => {
  const values = condition.values.filter((val: any) => val !== null);
  const hasNull = condition.values.includes(null);
  const apiFilters: any[] = [];

  if (values.length > 0) {
    apiFilters.push({
      field: filterKey,
      data_type: apiDataType,
      operator: "not_in",
      value: values,
    });
  }

  // not_in keeps NULL rows by design, so excluding the null bucket
  // needs an explicit has_data filter.
  if (hasNull) {
    apiFilters.push({
      field: filterKey,
      data_type: apiDataType,
      operator: "has_data",
      value: null,
    });
  }

  return apiFilters;
};

// text_array fields hold Postgres arrays: "equals" means exact set match on the
// backend, so membership filtering from the multi-select needs "contains".
const getDefaultTextOperator = (apiDataType: string) =>
  apiDataType === "text_array" ? "contains" : "equals";

// The backend only supports no_data (not is_null) for text and text_array
const getNullOperator = (apiDataType: string) =>
  apiDataType === "text" || apiDataType === "text_array"
    ? "no_data"
    : "is_null";

const resolveDateFilterCondition = (
  condition: DateFilterCondition,
): DateFilterCondition => {
  if (condition.mode === "relative" && condition.relativePreset) {
    const [startDate, endDate] = getRelativeDatePresetRange(
      condition.relativePreset,
    );

    return {
      ...condition,
      startDate,
      endDate,
    };
  }

  return condition;
};

const normalizeFiltersToApiFormat = ({
  filters = {},
  allFiltersData = {},
}: {
  filters?: any;
  allFiltersData?: any;
}) => {
  const apiFilters: any[] = [];

  Object.entries(filters).forEach(
    ([filterKey, filterConditions]: [string, any]) => {
      if (!filterConditions || filterConditions.length === 0) {
        return;
      }

      const dataType = allFiltersData[filterKey]?.data_type || "text";
      const apiDataType = dataType === "categorical" ? "text" : dataType;

      filterConditions.forEach((condition: any) => {
        if (condition === null) {
          const nullOperator = getNullOperator(apiDataType);
          apiFilters.push({
            field: filterKey,
            data_type: apiDataType,
            operator: nullOperator,
            value: null,
          });
          return;
        }

        // Geo conditions are detected by shape (not metadata data_type) so the
        // filter serializes correctly even before the backend exposes the geo
        // field in `/filters/` metadata. UI-only fields (display_unit, label)
        // are intentionally not sent.
        if (isGeoFilterCondition(condition)) {
          apiFilters.push({
            field: filterKey,
            data_type: GEO_DATA_TYPE,
            operator: condition.condition,
            value: {
              lat: condition.lat,
              lng: condition.lng,
              radius_km: condition.radius_km,
            },
          });
          return;
        }

        if (isCategoricalExclusionFilter([condition])) {
          apiFilters.push(
            ...buildExclusionApiFilters(filterKey, condition, apiDataType),
          );
        } else if (isCategoricalFilter(condition)) {
          const values = condition.filter((val: any) => val !== null);
          const hasNull = condition.includes(null);

          if (values.length > 0) {
            if (apiDataType === "text_array") {
              // The backend rejects "in" for text_array; per-value contains
              // filters are OR'd per field, giving the same any-of semantics
              values.forEach((value: any) => {
                apiFilters.push({
                  field: filterKey,
                  data_type: apiDataType,
                  operator: "contains",
                  value,
                });
              });
            } else {
              apiFilters.push({
                field: filterKey,
                data_type: apiDataType,
                operator: "in",
                value: values,
              });
            }
          }

          if (hasNull) {
            const nullOperator = getNullOperator(apiDataType);
            apiFilters.push({
              field: filterKey,
              data_type: apiDataType,
              operator: nullOperator,
              value: null,
            });
          }
        } else if (isNumericFilter([condition]) && dataType === "numeric") {
          const filter: any = {
            field: filterKey,
            data_type: "numeric",
            operator: condition.condition,
          };

          if (condition.condition === "between") {
            filter.min_value = condition.min;
            filter.max_value = condition.max;
          } else if (
            condition.condition === "has_data" ||
            condition.condition === "no_data"
          ) {
            filter.value = null;
          } else {
            filter.value = condition.value;
          }

          apiFilters.push(filter);
        } else if (isDateFilter([condition]) && dataType === "datetime") {
          const normalizedCondition = resolveDateFilterCondition(condition);

          const filter: any = {
            field: filterKey,
            data_type: "datetime",
            operator: normalizedCondition.condition,
          };

          if (normalizedCondition.condition === "no_data") {
            // no date fields needed for IS NULL
          } else if (normalizedCondition.condition === "between") {
            filter.start_date = formatDateToYYYYMMDD(
              normalizedCondition.startDate,
            );
            filter.end_date = formatDateToYYYYMMDD(normalizedCondition.endDate);
          } else {
            filter.value = formatDateToYYYYMMDD(normalizedCondition.value);
          }

          apiFilters.push(filter);
        } else {
          const value = condition.value || condition;
          const filter = createFilterWithBooleanCheck(
            filterKey,
            condition.condition || getDefaultTextOperator(apiDataType),
            value,
            apiDataType,
          );
          apiFilters.push(filter);
        }
      });
    },
  );

  return apiFilters;
};

/**
 * Constructs query parameters for GET requests from filters.
 */
export const constructQueryParams = ({
  filters = {},
  page = undefined,
  pageSize = undefined,
}: {
  filters?: {
    [key: string]: (string | null)[];
  };
  page?: number;
  pageSize?: number;
}) => {
  let queryParams = "";
  if (page && pageSize) {
    queryParams += `page=${page}&page_size=${pageSize}&`;
  }
  const areFiltersEmpty = checkFiltersAreEmpty(filters);

  if (!areFiltersEmpty) {
    let filtersArray: string[] = [];
    Object.keys(filters)?.forEach((key) => {
      if (
        filters[key] &&
        Array.isArray(filters[key]) &&
        filters[key].length > 0
      ) {
        // Filter out empty/null/undefined values and only process valid values
        const validValues = filters[key].filter(
          (value): value is string =>
            value !== null && value !== undefined && value !== "",
        );

        if (validValues.length > 0) {
          filtersArray = filtersArray.concat(
            validValues.map((value) => `${key}=${encodeURIComponent(value)}`),
          );
        }

        // Only add null filter if explicitly included in original array
        const hasExplicitNull = filters[key].some((v) => v === null);
        if (hasExplicitNull) {
          filtersArray.push(`${key}_is_null=true`);
        }
      }
    });
    queryParams += `${filtersArray.join("&")}`;
  }

  return queryParams;
};

/**
 * Constructs a complete query string for GET requests with proper formatting
 * Returns either "?" for no parameters, "?param=value" for single params, or "?param1=value1&param2=value2" for multiple
 */
export const constructQueryString = ({
  baseParams = {},
  filters = {},
  page = undefined,
  pageSize = undefined,
}: {
  baseParams?: { [key: string]: string | number };
  filters?: {
    [key: string]: (string | null)[];
  };
  page?: number;
  pageSize?: number;
}) => {
  const params: string[] = [];

  // Add base parameters (like data_type)
  Object.entries(baseParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  // Add filter parameters
  const filterParams = constructQueryParams({
    filters,
    page,
    pageSize,
  });
  if (filterParams) {
    params.push(filterParams);
  }

  // Return proper query string format
  return params.length > 0 ? `?${params.join("&")}` : "?";
};

/**
 * Prepares the POST request body for unified data API requests
 * Converts the current filter structure to a normalized API format
 */
export const prepareUnifiedDataPostRequestBody = ({
  global_filters,
  filters,
  allFiltersData = {},
  page = 1,
  pageSize = 100,
  sorting = undefined,
}: {
  global_filters?: any;
  filters?: any;
  allFiltersData?: any;
  page?: number;
  pageSize?: number;
  sorting?: SortItem[];
}) => {
  const resolvedGlobalFilters = global_filters ?? filters ?? {};
  const apiFilters = normalizeFiltersToApiFormat({
    filters: resolvedGlobalFilters,
    allFiltersData,
  });

  const requestBody = {
    global_filters: apiFilters,
    pagination: {
      page: page,
      page_size: pageSize,
    },
    sort: transformSortingToApiFormat(sorting),
  };
  return requestBody;
};

/**
 * Prepares the POST request body for dashboard plot requests.
 * Converts both global and plot filters to normalized API formats.
 */
export const prepareDashboardPostRequestBody = ({
  global_filters,
  plot_filters = {},
  filters,
  allFiltersData = {},
  page = 1,
  pageSize = 100,
  sorting = undefined,
}: {
  global_filters?: any;
  plot_filters?: any;
  filters?: any;
  allFiltersData?: any;
  page?: number;
  pageSize?: number;
  sorting?: SortItem[];
}) => {
  const resolvedGlobalFilters = global_filters ?? filters ?? {};
  const normalizedGlobalFilters = normalizeFiltersToApiFormat({
    filters: resolvedGlobalFilters,
    allFiltersData,
  });
  const normalizedPlotFilters = normalizeFiltersToApiFormat({
    filters: plot_filters,
    allFiltersData,
  });

  return {
    global_filters: normalizedGlobalFilters,
    plot_filters: normalizedPlotFilters,
    pagination: {
      page,
      page_size: pageSize,
    },
    sort: transformSortingToApiFormat(sorting),
  };
};

/**
 * Constructs query parameters for GET filters/data/ endpoint
 * Formats filters and pagination as JSON query parameters
 */
export const constructTrialDataQueryParams = ({
  filters = {},
  allFiltersData = {},
  pagination = undefined,
  datasetType = undefined,
}: {
  filters?: any;

  allFiltersData?: any;
  pagination?: { page: number; page_size: number };
  datasetType?: string;
}) => {
  const params: string[] = [];

  // Add dataset_type parameter
  if (datasetType) {
    params.push(`dataset_type=${encodeURIComponent(datasetType)}`);
  }

  // Add pagination parameter as JSON if provided
  if (pagination) {
    const paginationJson = JSON.stringify(pagination);
    params.push(`pagination=${encodeURIComponent(paginationJson)}`);
  }

  // Convert filters to API format if they exist
  const areFiltersEmpty = checkFiltersAreEmpty(filters);
  if (!areFiltersEmpty) {
    const apiFilters: any[] = [];

    Object.entries(filters).forEach(
      ([filterKey, filterConditions]: [string, any]) => {
        // Skip empty filters
        if (!filterConditions || filterConditions.length === 0) {
          return;
        }

        const dataType = allFiltersData[filterKey]?.data_type || "text";
        // Map categorical to text for API compatibility
        const apiDataType = dataType === "categorical" ? "text" : dataType;

        // Handle the case where filterConditions is a simple array like ["S-001", "S-002"]
        if (Array.isArray(filterConditions)) {
          // Check if this is a categorical filter (array of simple values)
          if (isCategoricalFilter(filterConditions)) {
            const values = filterConditions.filter((val: any) => val !== null);
            const hasNull = filterConditions.includes(null);

            if (values.length > 0) {
              values.forEach((value: any) => {
                apiFilters.push({
                  field: filterKey,
                  operator: getDefaultTextOperator(apiDataType),
                  data_type: apiDataType,
                  value: value,
                });
              });
            }

            // Handle null values separately
            if (hasNull) {
              const nullOperator = getNullOperator(apiDataType);
              apiFilters.push({
                field: filterKey,
                operator: nullOperator,
                data_type: apiDataType,
                value: null,
              });
            }
          } else {
            // Handle array of complex filter conditions

            filterConditions.forEach((condition: any) => {
              // Skip invalid conditions
              if (condition === null || condition === undefined) {
                return;
              }

              if (isCategoricalExclusionFilter([condition])) {
                apiFilters.push(
                  ...buildExclusionApiFilters(
                    filterKey,
                    condition,
                    apiDataType,
                  ),
                );
              } else if (
                isNumericFilter([condition]) &&
                dataType === "numeric"
              ) {
                // Handle numeric filters: {condition: "between", min: 5, max: 10}

                const filter: any = {
                  field: filterKey,
                  data_type: "numeric",
                  operator: condition.condition,
                };

                if (condition.condition === "between") {
                  filter.min_value = condition.min;
                  filter.max_value = condition.max;
                } else {
                  filter.value = condition.value;
                }

                apiFilters.push(filter);
              } else if (isDateFilter([condition]) && dataType === "datetime") {
                const normalizedCondition =
                  resolveDateFilterCondition(condition);
                // Handle date filters: {condition: "between", startDate: "2024-01-01", endDate: "2024-12-31"}

                const filter: any = {
                  field: filterKey,
                  data_type: "datetime",
                  operator: normalizedCondition.condition,
                };

                if (normalizedCondition.condition === "no_data") {
                  // no date fields needed for IS NULL
                } else if (normalizedCondition.condition === "between") {
                  filter.start_date = formatDateToYYYYMMDD(
                    normalizedCondition.startDate,
                  );
                  filter.end_date = formatDateToYYYYMMDD(
                    normalizedCondition.endDate,
                  );
                } else {
                  filter.value = formatDateToYYYYMMDD(
                    normalizedCondition.value,
                  );
                }

                apiFilters.push(filter);
              } else {
                // Handle text filters or simple values
                // Check if condition is an object with a condition property
                if (!(typeof condition === "object" && condition.condition)) {
                  // Handle simple string/number/boolean values
                  const filter = createFilterWithBooleanCheck(
                    filterKey,
                    getDefaultTextOperator(apiDataType),
                    condition,
                    apiDataType,
                  );
                  apiFilters.push(filter);
                  return;
                }

                const filter = createFilterWithBooleanCheck(
                  filterKey,
                  condition.condition,
                  condition.value,
                  apiDataType,
                );
                apiFilters.push(filter);
              }
            });
          }
        }
      },
    );

    // Add filters as JSON parameter if we have any
    if (apiFilters.length > 0) {
      const filtersJson = JSON.stringify(apiFilters);
      params.push(`filters=${encodeURIComponent(filtersJson)}`);
    }
  }

  return params.length > 0 ? params.join("&") : "";
};
